/* ==========================================================================
   Sultannoten UI behaviour (no libraries)
   - carousels: scroll-snap track, arrows, dots
   - product cards: AJAX add to cart → theme cart drawer (window.refreshCart)
   - header: mega menu (hover/focus/click, Esc, overlay), compact sticky bar,
     mobile search panel, drawer drill-down, sticky offsets on mobile
   - announcement rotation, promo bar dismiss, footer accordions, video
   Cart drawer, inert handling and predictive search stay with the theme
   (assets/section-header.js, component-cart.js).
   ========================================================================== */
(() => {
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const strings = window.snStrings || {};
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  const announce = (text) => {
    const live = document.getElementById('screen-reader-info');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => { live.textContent = text; }, 50);
  };

  /* ---------- Carousels ---------- */
  const initCarousel = (root) => {
    if (root.snCarousel) return;
    const track = root.querySelector('[data-sn-track]');
    if (!track) return;
    root.snCarousel = true;
    const prev = root.querySelector('[data-sn-prev]');
    const next = root.querySelector('[data-sn-next]');
    const dots = root.querySelector('[data-sn-dots-list]');

    const metrics = () => {
      const first = track.children[0];
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const width = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
      const visible = Math.max(1, Math.floor((track.clientWidth + gap + 1) / width));
      return { width, visible };
    };
    const maxScroll = () => track.scrollWidth - track.clientWidth;
    const pageCount = () => {
      const { visible } = metrics();
      return Math.max(1, Math.ceil((track.children.length - visible) / visible) + 1);
    };

    const buildDots = () => {
      if (!dots) return;
      const count = pageCount();
      if (dots.children.length === count) return;
      dots.innerHTML = '';
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sn-dot';
        dot.setAttribute('aria-label', (strings.goToSlide || '{n}').replace('{n}', i + 1));
        dot.addEventListener('click', () => {
          const { width, visible } = metrics();
          track.scrollTo({ left: Math.min(i * visible * width, maxScroll()), behavior: behavior() });
        });
        dots.appendChild(dot);
      }
    };

    const update = () => {
      const left = track.scrollLeft;
      const overflow = maxScroll() > 2;
      root.classList.toggle('has-overflow', overflow);
      if (prev) prev.disabled = left <= 2;
      if (next) next.disabled = left >= maxScroll() - 2;
      if (dots && dots.children.length) {
        const { width, visible } = metrics();
        let page = Math.round(left / (visible * width));
        if (left >= maxScroll() - 2) page = dots.children.length - 1;
        Array.from(dots.children).forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
      }
    };

    const move = (dir) => {
      const { width, visible } = metrics();
      track.scrollBy({ left: dir * Math.max(1, visible) * width, behavior: behavior() });
    };
    if (prev) prev.addEventListener('click', () => move(-1));
    if (next) next.addEventListener('click', () => move(1));

    let frame;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    }, { passive: true });
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => { buildDots(); update(); }).observe(track);
    }
    buildDots();
    update();
  };

  /* ---------- Add to cart (product cards) ---------- */
  const updateCountsFallback = () => fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
    .then((r) => r.json())
    .then((cart) => {
      document.querySelectorAll('[data-header-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    })
    .catch(() => {});

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-sn-atc]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const label = form.querySelector('[data-sn-atc-label]');
    if (!button || button.disabled || button.classList.contains('is-loading')) return;
    const original = label.textContent;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    label.textContent = strings.adding || original;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }] }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || strings.error);
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      label.textContent = strings.added || original;
      announce(strings.added || '');
      if (typeof window.refreshCart === 'function') {
        window.refreshCart(true);
      } else {
        updateCountsFallback();
      }
      window.setTimeout(() => {
        button.classList.remove('is-added');
        label.textContent = original;
      }, 2500);
    } catch (error) {
      button.classList.remove('is-loading');
      label.textContent = error.message || strings.error || original;
      announce(label.textContent);
      window.setTimeout(() => { label.textContent = original; }, 3000);
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Sticky offsets (mobile: announcement + header + promo) ---------- */
  const setStickyVars = () => {
    const ann = document.querySelector('.sn-sec-ann');
    const hdr = document.querySelector('.sn-sec-hdr');
    const style = document.documentElement.style;
    style.setProperty('--sn-ann-h', `${ann && !ann.hidden ? ann.offsetHeight : 0}px`);
    style.setProperty('--sn-hdr-h', `${hdr ? hdr.offsetHeight : 0}px`);
  };
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(setStickyVars);
    document.querySelectorAll('.sn-sec-ann, .sn-sec-hdr').forEach((el) => ro.observe(el));
  }
  setStickyVars();

  /* ---------- Header ---------- */
  const initHeader = (section) => {
    if (!section || section.snHeader) return;
    section.snHeader = true;

    // drawer lives on <body> so no sticky/z-index context traps it under the overlay
    const drawer = section.querySelector('#sn-menu-drawer');
    if (drawer) {
      document.querySelectorAll('body > #sn-menu-drawer').forEach((old) => { if (old !== drawer) old.remove(); });
      document.body.appendChild(drawer);
    }

    // mega menu
    const nav = section.querySelector('[data-sn-nav]');
    const overlay = section.querySelector('[data-sn-mega-overlay]');
    let openItem = null;
    let openTimer;
    let closeTimer;
    let touched = false;
    const setState = (item, on) => {
      item.classList.toggle('is-open', on);
      const toggle = item.querySelector('[data-sn-mega-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(on));
    };
    const open = (item) => {
      clearTimeout(closeTimer);
      if (openItem && openItem !== item) setState(openItem, false);
      setState(item, true);
      openItem = item;
      section.classList.add('sn-mega-open');
    };
    const close = () => {
      clearTimeout(openTimer);
      if (!openItem) return;
      setState(openItem, false);
      openItem = null;
      section.classList.remove('sn-mega-open');
    };
    if (nav) {
      nav.addEventListener('touchstart', () => { touched = true; }, { passive: true });
      nav.querySelectorAll('[data-sn-nav-item]').forEach((item) => {
        item.addEventListener('mouseenter', () => {
          if (!desktop.matches) return;
          clearTimeout(closeTimer);
          clearTimeout(openTimer);
          openTimer = window.setTimeout(() => open(item), openItem ? 0 : 150);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(openTimer);
          closeTimer = window.setTimeout(close, 200);
        });
        const toggle = item.querySelector('[data-sn-mega-toggle]');
        if (toggle) toggle.addEventListener('click', () => (item.classList.contains('is-open') ? close() : open(item)));
        const link = item.querySelector('.sn-nav__link');
        if (link) {
          link.addEventListener('click', (event) => {
            if (touched && !item.classList.contains('is-open')) {
              event.preventDefault();
              open(item);
            }
          });
        }
        item.addEventListener('focusout', (event) => {
          if (openItem === item && !item.contains(event.relatedTarget)) close();
        });
      });
    }
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !openItem) return;
      const toggle = openItem.querySelector('[data-sn-mega-toggle]');
      close();
      if (toggle) toggle.focus();
    });
    desktop.addEventListener('change', close);

    // compact sticky bar (desktop)
    const sticky = section.querySelector('[data-sn-sticky]');
    if (sticky && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        const show = desktop.matches && !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        if (show) close();
      });
      io.observe(section.querySelector('.sn-hdr__main') ? section : sticky);
    }

    // mobile search panel
    const searchToggle = section.querySelector('[data-sn-msearch-toggle]');
    const searchPanel = section.querySelector('[data-sn-msearch]');
    const setSearch = (on, returnFocus) => {
      if (!searchPanel || !searchToggle) return;
      searchPanel.hidden = !on;
      searchToggle.setAttribute('aria-expanded', String(on));
      if (on) {
        const input = searchPanel.querySelector('input[type="search"]');
        if (input) input.focus();
      } else {
        const overlayEl = searchPanel.querySelector('.search-results-overlay');
        if (overlayEl) overlayEl.classList.remove('active');
        if (returnFocus) searchToggle.focus();
      }
      setStickyVars();
    };
    if (searchToggle) searchToggle.addEventListener('click', () => setSearch(searchPanel.hidden, false));
    const searchClose = section.querySelector('[data-sn-msearch-close]');
    if (searchClose) searchClose.addEventListener('click', () => setSearch(false, true));
    if (searchPanel) {
      searchPanel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setSearch(false, true);
      });
    }

    // drawer drill-down
    const menuDrawer = document.getElementById('sn-menu-drawer');
    if (menuDrawer && !menuDrawer.snInit) {
      menuDrawer.snInit = true;
      const root = menuDrawer.querySelector('#sn-dp-root');
      const show = (from, to, dir) => {
        from.hidden = true;
        from.classList.remove('is-active');
        to.hidden = false;
        to.classList.add('is-active');
        to.classList.remove('sn-anim-right', 'sn-anim-left');
        void to.offsetWidth; // restart the animation
        to.classList.add(dir === 'right' ? 'sn-anim-right' : 'sn-anim-left');
        const body = menuDrawer.querySelector('.sn-drawer__body');
        if (body) body.scrollTop = 0;
        menuDrawer.scrollTop = 0;
      };
      menuDrawer.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-sn-panel-open]');
        if (opener) {
          const target = menuDrawer.querySelector(`#${opener.getAttribute('aria-controls')}`);
          if (!target) return;
          show(opener.closest('[data-sn-panel]'), target, 'right');
          opener.setAttribute('aria-expanded', 'true');
          const back = target.querySelector('[data-sn-panel-back]');
          if (back) back.focus();
          return;
        }
        const back = event.target.closest('[data-sn-panel-back]');
        if (back && root) {
          const current = back.closest('[data-sn-panel]');
          show(current, root, 'left');
          const origin = root.querySelector(`[aria-controls="${current.id}"]`);
          if (origin) {
            origin.setAttribute('aria-expanded', 'false');
            origin.focus();
          }
        }
      });
      // focus the close button on open, reset to level 1 after closing
      new MutationObserver(() => {
        if (menuDrawer.classList.contains('sidebar--opened')) {
          const closeBtn = menuDrawer.querySelector('[data-js-close]');
          if (closeBtn && !menuDrawer.contains(document.activeElement)) closeBtn.focus({ preventScroll: true });
        } else if (root) {
          window.setTimeout(() => {
            menuDrawer.querySelectorAll('[data-sn-panel]').forEach((panel) => {
              const isRoot = panel === root;
              panel.hidden = !isRoot;
              panel.classList.toggle('is-active', isRoot);
              panel.classList.remove('sn-anim-right', 'sn-anim-left');
            });
            root.querySelectorAll('[aria-expanded="true"]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
          }, 350);
        }
      }).observe(menuDrawer, { attributes: true, attributeFilter: ['class'] });
    }
  };

  /* ---------- Announcement ---------- */
  const initAnnouncement = (el) => {
    if (el.snInit) return;
    el.snInit = true;
    const messages = Array.from(el.querySelectorAll('[data-sn-ann-msg]'));
    if (messages.length < 2) return;
    let index = 0;
    let timer;
    const show = (n) => {
      messages[index].classList.remove('is-active');
      messages[index].setAttribute('aria-hidden', 'true');
      index = (n + messages.length) % messages.length;
      messages[index].classList.add('is-active');
      messages[index].removeAttribute('aria-hidden');
    };
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (reduceMotion.matches) return;
      timer = window.setInterval(() => show(index + 1), (parseInt(el.dataset.interval, 10) || 5) * 1000);
    };
    const prev = el.querySelector('[data-sn-ann-prev]');
    const next = el.querySelector('[data-sn-ann-next]');
    if (prev) prev.addEventListener('click', () => { show(index - 1); start(); });
    if (next) next.addEventListener('click', () => { show(index + 1); start(); });
    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', start);
    el.addEventListener('focusin', stop);
    el.addEventListener('focusout', start);
    start();
    el.snShow = (blockEl) => {
      const i = messages.indexOf(blockEl);
      if (i > -1) { stop(); show(i); }
    };
  };

  /* dialogs (announcement ⊕) and small dropdowns */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-sn-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.snDialogOpen);
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      return;
    }
    const closer = event.target.closest('[data-sn-dialog-close]');
    if (closer) {
      closer.closest('dialog').close();
      return;
    }
    if (event.target.matches('dialog.sn-dialog')) event.target.close(); // click on backdrop
    document.querySelectorAll('[data-sn-dropdown][open]').forEach((d) => {
      if (!d.contains(event.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-sn-dropdown][open]').forEach((d) => {
      d.removeAttribute('open');
      const summary = d.querySelector('summary');
      if (summary && d.contains(document.activeElement)) summary.focus();
    });
  });

  /* ---------- Promo bar ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sn-promo-close]');
    if (!button) return;
    const bar = button.closest('[data-sn-promo]');
    try {
      (bar.dataset.store === 'local' ? localStorage : sessionStorage).setItem(bar.dataset.key, '1');
    } catch (e) { /* storage blocked: close for this page view only */ }
    const section = bar.closest('.shopify-section') || bar;
    section.hidden = true;
    setStickyVars();
    const target = document.querySelector('main a, main button');
    if (target && document.activeElement === document.body) target.focus({ preventScroll: true });
  });

  /* ---------- Footer accordions: open (and fixed) on desktop ---------- */
  const syncFooter = () => {
    document.querySelectorAll('[data-sn-footer-acc]').forEach((details) => {
      if (desktop.matches) details.open = true;
    });
  };
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('[data-sn-footer-acc] > summary');
    if (summary && desktop.matches) event.preventDefault();
  });
  desktop.addEventListener('change', (event) => {
    if (event.matches) {
      syncFooter();
    } else {
      document.querySelectorAll('[data-sn-footer-acc]').forEach((d) => { d.open = false; });
    }
  });

  /* ---------- Brand story video: play while visible, pause button ---------- */
  const initVideo = (wrap) => {
    if (wrap.snInit) return;
    wrap.snInit = true;
    const video = wrap.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.setAttribute('aria-hidden', 'true');
    video.removeAttribute('autoplay');
    const toggle = wrap.querySelector('[data-sn-video-toggle]');
    let paused = reduceMotion.matches;
    let visible = false;
    const sync = () => {
      if (toggle) toggle.setAttribute('aria-pressed', String(paused));
      if (visible && !paused) {
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        video.pause();
      }
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); }, { threshold: 0.2 }).observe(wrap);
    }
    if (toggle) toggle.addEventListener('click', () => { paused = !paused; sync(); });
    sync();
  };

  /* ---------- Init + theme editor ---------- */
  const init = (scope = document) => {
    scope.querySelectorAll('[data-sn-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-sn-announcement]').forEach(initAnnouncement);
    scope.querySelectorAll('[data-sn-video]').forEach(initVideo);
    const header = scope.matches && scope.matches('.sn-sec-hdr') ? scope : scope.querySelector('.sn-sec-hdr');
    if (header) initHeader(header);
    syncFooter();
    setStickyVars();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const ann = event.target.closest('[data-sn-announcement]');
    if (ann && ann.snShow) ann.snShow(event.target);
  });
})();
